/**
 * Security — the half of the old Technical Specifications page a security
 * reviewer reads. Plain statements, each one true of production today; SOC 2
 * is stated as in process because it is.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Building2, FileText, KeyRound, Lock, ShieldCheck, UserCheck } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { contactHref } from '@/components/marketing/siteFacts';

const CONTROLS = [
  {
    icon: ShieldCheck,
    title: 'HIPAA and BAAs',
    body: 'WeaveIn signs a Business Associate Agreement with customers who share protected health information, and holds BAAs with the subprocessors that touch protected health information, including messaging, transcription and AI.',
  },
  {
    icon: FileText,
    title: 'SOC 2 in process',
    body: 'Controls are being audited toward a SOC 2 report. We share the current control set and evidence under NDA during a security review.',
  },
  {
    icon: Building2,
    title: 'Each organization isolated',
    body: 'Every record belongs to one organization, and every query is scoped to it. An API key is pinned to one organization; a request header cannot widen what it sees.',
  },
  {
    icon: Lock,
    title: 'Consent, including 42 CFR Part 2',
    body: 'Consent is recorded per member and per exchange. Substance-use records are segmented so they are only shared under a Part 2 consent.',
  },
  {
    icon: KeyRound,
    title: 'Sign-in and sessions',
    body: 'SAML single sign-on, two-factor authentication, role-based access, per-device sessions you can revoke, and an idle lock on mobile that requires Face ID or a passcode.',
  },
  {
    icon: UserCheck,
    title: 'Every access on the record',
    body: 'Changes are audit-logged with who made them. Support access to a user’s view is limited to platform administrators, attributed to the person doing it, and logged.',
  },
  {
    icon: Bot,
    title: 'Agents draft. People approve.',
    body: 'An agent can draft a note, a code, a rule row or a submission. Nothing reaches a funder until a named person approves it, and every submission records who signed it.',
  },
];

export default function SecurityPage() {
  usePageMeta(SEO_ROUTES['/security']);

  return (
    <div className="bg-white dark:bg-gray-950">
      <MarketingHeader />

      <section className="bg-paper border-b border-v2border text-ink">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-4">Security</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Built for protected health information<br />
            <span className="text-v2accent">and for the people it belongs to.</span>
          </h1>
          <p className="text-xl text-ink-500 max-w-3xl">
            What a security reviewer needs, stated plainly. Ask us for anything that is not here.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CONTROLS.map((c) => (
            <div key={c.title} className="rounded-xl border border-gray-200 dark:border-gray-800 p-6">
              <c.icon className="w-6 h-6 text-v2accent mb-3" aria-hidden="true" />
              <h2 className="font-bold text-gray-900 dark:text-gray-100 mb-2">{c.title}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Starting a security review?</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            We will send the questionnaire answers, the BAA and the subprocessor list. Integration detail is on{' '}
            <Link to="/developers" className="text-ink hover:underline">Developers</Link>.
          </p>
          <Link
            to={contactHref('general', 'security review')}
            className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
          >
            Request the security packet
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
